import { cloneDeep } from "lodash";
import moment from "moment";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { reportIRBOFilterOptions, reportIRBOIndicators } from "../report-irbo";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { ReportIRBOFilterSettingsForm } from "../settings-form/ReportIRBOFilterSettingsForm";
import { ReportIRBOFilterSettings } from "../settings/ReportIRBOFilterSettings";
import { ReportIRBOResponse } from "../other-types/ReportIRBOResponse";
import { ReportIRBOTableDataRequest } from "../other-types/ReportIRBOTableDataRequest";
import { ReportIRBOTableData } from "../table-data/table-data-types";
import { ReportIRBOJournalRequest } from "../journal/ReportIRBOJournalRequest";
import { Option } from "../../../common/types/Option";
import { MutuallyRedeemingLevel } from "../../../common/types/roots/fields";

export class ReportIRBOFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(reportIRBOFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        if (settings.reportName !== 'IRBO') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== 'IRBO') {
            throw new Error('error_type_filter_options')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        reportType = { ...reportType }

        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('error_not_found_data_source')
        }
        dataSource = { ...dataSource }

        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('error_not_found_periodicity')
        }
        periodicity = { ...periodicity }

        let regionOption = options.regions.find(it => it.value === settings.region)
        if (!regionOption) {
            throw new Error('error_not_found_region_option')
        }
        regionOption = { ...regionOption }

        
        let mutuallyRedeemingOption = options.mutuallyRedeeming.find(it => it.value === settings.mutuallyRedeeming)
        if (!mutuallyRedeemingOption) {
            throw new Error('error_not_found_mutually_redeeming_option')
        }
        mutuallyRedeemingOption = { ...mutuallyRedeemingOption }

        const mutuallyRedeemingLevelOptions: Option<MutuallyRedeemingLevel>[] = []
        settings.mutuallyRedeemingLevels.forEach(level => {
            const option = options.mutuallyRedeemingLevels.find(it => it.value === level)!
            mutuallyRedeemingLevelOptions.push({...option})
        })

        let measureUnitOption = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnitOption) {
            throw new Error('error_not_found_measure_unit')
        }
        measureUnitOption = { ...measureUnitOption } 

        let isRoundUp: boolean = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }

        // 2. Формирование
        const form: ReportIRBOFilterSettingsForm = {
            key: 'IRBO',

            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,

            region: regionOption,
            date: new Date(settings.date),
            measureUnit: measureUnitOption,
            mutuallyRedeeming: mutuallyRedeemingOption,
            mutuallyRedeemingLevels: mutuallyRedeemingLevelOptions,
            decodingActualRegionalBudget: settings.decodingActualRegionalbudget,
            roundUp: isRoundUp,
            roundUpTo: settings.roundUpTo ?? null,

            indicators: cloneDeep(settings.indicators),
            sortByRegionCode: settings.sortByRegionCode ?? false,
        }

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'IRBO') {
            throw new Error('error_type_filter_settingsForm')
        }
        
        if (form.region == null) {
            throw new Error('error_input_region')
        }

        if (form.mutuallyRedeeming == null) {
            throw new Error('error_input_mutually_redeeming')
        }
        if (form.measureUnit == null) {
            throw new Error('error_input_measure_unit')
        }

        if (!form.date) {
            throw new Error('error_input_date')
        }
        const strDate = moment(form.date).format('YYYY-MM-DD')

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const settings: ReportIRBOFilterSettings = {
            reportName: 'IRBO',

            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,

            region: form.region.value,
            date: strDate,
            measureUnit: form.measureUnit.value,
            mutuallyRedeeming: form.mutuallyRedeeming.value,
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels?.map(it => it.value) ?? [],
            decodingActualRegionalbudget: form.decodingActualRegionalBudget ?? false,
            roundUpTo: roundUpTo,
            indicators: cloneDeep(form.indicators),
            sortByRegionCode: form.sortByRegionCode ?? false,
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(reportIRBOFilterOptions)

        const yesterdayMoment = moment().subtract(1, 'days')
        const yesterdayDate = yesterdayMoment.toDate()

        const emptyForm: ReportIRBOFilterSettingsForm = {
            key: 'IRBO',

            reportName: options.reportNames.find(it => it.value === 'IRBO')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'MIXED')!,
            dataSource: options.dataSources.find(it => it.value === 'IISK')!,
            periodicity: options.periodicities.find(it => it.value === 'DAY')!,
            
            region: null,
            date: yesterdayDate,
            mutuallyRedeeming: options.mutuallyRedeeming.find(it => it.value === 'WITHOUT')!,
            mutuallyRedeemingLevels: [],
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            decodingActualRegionalBudget: true,
            roundUp: false,
            roundUpTo: null,
            indicators: cloneDeep(reportIRBOIndicators),
            sortByRegionCode: false,
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидаций
        if (form.key !== 'IRBO') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (form.region == null) {
            throw new Error('error_input_region')
        }
        if (!form.date) {
            throw new Error('error_input_date')
        }
        if (form.measureUnit == null) {
            throw new Error('error_input_measure_unit')
        }
        if (form.mutuallyRedeeming == null) {
            throw new Error('error_input_mutually_redeeming')
        }
        if (form.decodingActualRegionalBudget == null) {
            throw new Error('error_input_decoding_actual_regional_budget')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo
        }

        const strDate = moment(form.date).format('YYYY-MM-DD')

        // 2. Формирование данных для таблицы
        const params: ReportIRBOTableDataRequest = {
            region: form.region.value,
            report_date: strDate,
            mutually_redeeming: form.mutuallyRedeeming.value,
            mutually_redeeming_levels: form.mutuallyRedeemingLevels.map(it => it.value),
            measure_unit: form.measureUnit.value,
            round_up_to: roundUpTo,
            decoding_actual_regional_budget: form.decodingActualRegionalBudget,
            sort_by_region_code: form.sortByRegionCode ?? false,
            data_source: form.dataSource.value,
        }

        const response = await fetch('/api-py/monitoring/reports-constructor/report-irbo',
            {
                method: 'POST',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }
        const responseData: ReportIRBOResponse = await response.json()
        if ('template2_items' in responseData) {
            responseData.template2_items = responseData.template2_items!.map(item => ({
                ...item,
                is_expanded: true
            }))
        };
        const tableData: ReportIRBOTableData = responseData

        // 3. Формирование параметров для выгрузки журнал
        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const journalParams: ReportIRBOJournalRequest = {
            reportName: "IRBO",

            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            indicators_order: form.indicators.filter(it => it.active).map(it => it.key),

            region: form.region.value,
            report_date: strDate,
            measure_unit: form.measureUnit.value,
            actually_regional: tableData.actually_regional ?? null,
            region_budget: tableData.region_budget ?? null,
            template1_items: tableData.template1_items ?? null,
            template1_items_districts_total: tableData.template1_items_districts_total ?? null,
            template1_items_total: tableData.template1_items_total ?? null,
            template1_regional_budget_decrypt_items: tableData.template1_regional_budget_decrypt_items ?? null,
            template2_items: tableData.template2_items ?? null,
            template2_items_districts_total: tableData.template2_items_districts_total ?? null,
            template2_items_total: tableData.template2_items_total ?? null,
            decoding_actual_regional_budget: form.decodingActualRegionalBudget,
        }

        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(tableData),
            journalParams: journalParams
        }

        return result
    }
}
